import { colors as themeColors } from '../../utils/theme';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import axios from 'axios';
import { Box, Grid, Paper, Typography, IconButton, Snackbar, Alert, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import { VscNewFile } from "react-icons/vsc";
import { FaFileExcel } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';

export default function CandidateDetails({ connectionString }) {
    const navigate = useNavigate();
    const { user, selectedCompany } = useAuth();
    const companyId = selectedCompany?.CompId || null;
    const path = `${connectionString}/candidate`;
    const userId = user?.UserId || null;
    const [rows, setRows] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [selectionModel, setSelectionModel] = useState([]);
    const [loading, setLoading] = useState(false);
    const [onRefresh, setonRefresh] = useState(false);
    const location = useLocation();
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });

    useEffect(() => {
        const saved = sessionStorage.getItem("candidateSnackbar");
        if (saved) {
            const data = JSON.parse(saved);

            setSnackbar({
                open: true,
                message: data.message,
                severity: data.severity
            });

            sessionStorage.removeItem("candidateSnackbar");
        }
    }, []);



    useEffect(() => {
        fetchData();
        fetchTotalCount();
    }, [paginationModel]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const offset = paginationModel.page * paginationModel.pageSize;
            console.log(path);
            const res = await axios.get(
                `${path}/search?mode=search&offset=${offset}&limit=${paginationModel.pageSize}&companyId=${companyId}`,
                {
                    headers: {
                        'Cache-Control': 'no-store' // ⬅️ prevents intermediate caches from storing this request
                    },
                    validateStatus: status => [200, 204, 300, 304].includes(status)
                }
            );
            console.log('Fetch response:', res.data.data);
            if (res.status === 200 || res.status === 304 || res.status === 300) {
                setRows(res.data.data || []);
                setSelectionModel([]);
                if ((res.data.data || []).length === 0) {
                    setSnackbar({ open: true, message: 'No records found.', severity: 'info' });
                }
            } else {
                setRows([]);
                setSelectionModel([]);
                setSnackbar({ open: true, message: 'No records found.', severity: 'info' });
            }
        } catch (err) {
            console.error("Fetch error:", err);
            setSnackbar({ open: true, message: 'Failed to fetch data.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const fetchTotalCount = async () => {
        try {
            const res = await axios.get(`${path}/count`);
            setTotalCount(Number(res.data) || 0);
        } catch (error) {
            console.error('Error fetching client count:', error);
            setSnackbar({ open: true, message: 'Failed to fetch total count.', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (selectionModel.length === 0) return;
        try {
            console.log('Sending delete request for IDs:', selectionModel);
            const response = await axios.post(`${path}/delete`, { userId, objAryId: selectionModel });
            console.log('Delete response:', JSON.stringify(response.data, null, 2));
            setSnackbar({ open: true, message: response.data.message || 'Deleted successfully.', severity: 'success' });
            fetchData();
            fetchTotalCount();
        } catch (error) {
            console.error('Delete error:', error);
            console.log('Error response:', JSON.stringify(error.response, null, 2));
            let message = 'Delete failed due to an unexpected error.';
            if (error.response) {
                console.log('Error response data:', JSON.stringify(error.response.data, null, 2));
                if (error.response.status === 400) {
                    message = error.response.data.error || error.response.data.message || `Delete failed with status 400: ${JSON.stringify(error.response.data)}`;
                } else {
                    message = `Delete failed with status ${error.response.status}: ${error.response.data?.message || JSON.stringify(error.response.data)}`;
                }
            } else if (error.request) {
                console.log('No response received:', error.request);
                message = 'Delete failed: No response from server. Check if the server is running or the endpoint is correct.';
            } else {
                console.log('Error message:', error.message);
                message = `Delete failed: ${error.message}`;
            }
            setSnackbar({ open: true, message, severity: 'error' });
        }
    };

    const funExportData = async () => {
        try {
            const res = await axios({
                url: `${path}/search?mode=export`,
                method: 'GET',
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'exportedClients.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setSnackbar({ open: true, message: 'Export failed.', severity: 'error' });
        }
    };

    const columns = [
        {
            field: 'CandidateName', headerName: 'Candidate Name', flex: 1,
            renderCell: (params) => (
                <Typography sx={{ cursor: 'pointer', color: themeColors.primary.main, textDecoration: 'underline', fontSize: '0.85rem' }} onClick={() => navigate(`/main/editCandidate/${params.row.id}`)} >
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'FatherName', headerName: 'Father Name', flex: 0.8,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.85rem' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'MobileNo', headerName: 'Mobile No.', flex: 0.4,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.85rem' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'EmailId', headerName: 'Email Id', flex: 0.45,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '0.85rem' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'location', headerName: 'Location', flex: 0.45,
            renderCell: (params) => {
                const { City, State } = params.row;
                return (
                    <Box>
                        <Typography sx={{ fontSize: '0.75rem' }}>
                            {City || '—'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem' }}>
                            {State || '—'}
                        </Typography>
                    </Box>
                );
            }
        }
    ];

    if (user?.UserCategory !== "User") {
        columns.push({
            flex: 0.15,
            sortable: false,
            filterable: false,
            headerAlign: 'right',
            align: 'right',
            renderCell: (params) => {
                const isRowSelected = selectionModel.includes(params.row.id);
                return (
                    <Tooltip
                        title={isRowSelected ? 'Delete Selected' : 'Select row to enable'}
                    >
                        <span>
                            <IconButton
                                color="error"
                                onClick={handleDelete}
                                disabled={!isRowSelected}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                );
            },
        });
    }

    return (
        <Box sx={{ p: 1 }}>
            {/* Toolbar */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    alignItems: 'center',
                    width: '100%',
                    px: 1
                }}
            >
                {/* LEFT */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Add New Candidate">
                        <IconButton
                            component={RouterLink}
                            to="/main/CandidateMaster"
                            sx={{ color: 'orange' }}
                        >
                            <VscNewFile />
                        </IconButton>
                    </Tooltip>

                    {/* <Tooltip title="Export to Excel">
                        <IconButton onClick={funExportData} color="success">
                            <FaFileExcel />
                        </IconButton>
                    </Tooltip> */}
                </Box>

                {/* CENTER */}
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom>
                        Candidate Details
                    </Typography>
                </Box>

                {/* RIGHT */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" onClick={() => navigate("/main/insertCandidates")}>
                        <img
                            src="/upload.svg"
                            alt="Upload"
                            style={{ width: 34, height: 34 }}
                        />
                    </IconButton>
                </Box>
            </Box>

            {/* DataGrid */}
            <Paper elevation={0} sx={{ mt: 1, bgcolor: themeColors.neutral.surface, borderRadius: 2 }}>
                <Box sx={{ maxHeight: '84vh', maxWidth: '100%', overflow: 'auto' }}>
                    <DataGrid
                        autoHeight
                        rows={rows}
                        columns={columns}
                        getRowId={(r) => r.id}
                        checkboxSelection={user?.UserCategory !== "User"}
                        rowSelectionModel={selectionModel}
                        onRowSelectionModelChange={(newSel) => setSelectionModel(newSel.map(Number))}
                        disableSelectionOnClick
                        loading={loading}
                        pagination
                        pageSize={paginationModel.pageSize}
                        page={paginationModel.page}
                        rowCount={totalCount}
                        paginationMode="server"
                        onPageChange={(page) => setPaginationModel(p => ({ ...p, page }))}
                        onPageSizeChange={(ps) => setPaginationModel(p => ({ ...p, pageSize: ps }))}
                        rowsPerPageOptions={[50, 100]}
                        sx={{
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: themeColors.neutral.gridHeader, borderBottom: `1px solid ${themeColors.neutral.border}` },
                            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                            '& .MuiDataGrid-columnHeader': { backgroundColor: themeColors.neutral.gridHeader, color: themeColors.neutral.ink, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },

                            // Background styling
                            '& .MuiDataGrid-filler': { backgroundColor: themeColors.neutral.surface },
                            '& .MuiDataGrid-scrollbarFiller': { backgroundColor: themeColors.neutral.gridHeader },

                            // Row styling
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.neutral.surfaceAlt },
                            '& .MuiDataGrid-row:hover': { backgroundColor: themeColors.primary.tint },

                            // Selected row styling (with !important to override default MUI)
                            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: `${themeColors.primary.selected} !important` },

                            // Cell content alignment
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center'
                            },
                        }}
                    />
                </Box>
            </Paper>

            {/* Snackbar */}
            <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
            >
                <Alert
                    onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Box>
    );
}